import { ChangeEvent, forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { HexColorPicker } from 'react-colorful';
import { useOnClickOutside, useResizeObserver } from 'usehooks-ts';
import classNames from 'classnames';
import { debounce } from 'lodash';

import { useScreenSize } from '@/lib/hooks/use-screen-size';
import { Overlay } from '@/components/overlay';
import { Button, InlineButton } from '@/components/button';

import { FormInputSize } from '../types';
import { Input } from '../input';

import styles from './color-input.module.scss';

interface ColorPickerProps {
  color: string;
  presetColors?: string[];
  size?: FormInputSize;
  label?: string;
  onChange: (color: string | undefined) => void;
  className?: string;
}

const PositionPadding = 40;

type Position = {
  top: number;
  left: number;
};

// Validates HEX color format: #RGB, #RRGGBB, or #RRGGBBAA
function isValidHexColor(color: string): boolean {
  return /^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6}|[A-Fa-f0-9]{8})$/.test(color);
}

function ensureHexPrefix(color: string): string {
  return `#${color.replace(/^#+/g, '')}`;
}

const Popover = (props: {
  color: string;
  presetColors?: string[];
  position: Position;
  onChange: (color: string | undefined) => void;
  onClose: () => void;
}) => {
  const popover = useRef<HTMLDivElement>(null!);
  const isSelecting = useRef(false);

  const screenSize = useScreenSize();
  useOnClickOutside(popover, props.onClose);
  const popoverSize = useResizeObserver({
    ref: popover,
    box: 'content-box',
  });
  const [color, setColor] = useState(props.color);
  const [inputValue, setInputValue] = useState(props.color);

  useEffect(() => {
    const hexCode = ensureHexPrefix(props.color);
    setColor(hexCode);
    setInputValue(hexCode);
  }, [props.color]);

  const position = useMemo(() => {
    if (popoverSize.width === undefined || popoverSize.height === undefined) {
      return { top: 0, left: 0, opacity: 0 };
    }
    const left =
      props.position.left + popoverSize.width + PositionPadding > screenSize.width
        ? screenSize.width - popoverSize.width - PositionPadding
        : props.position.left;
    const top =
      props.position.top + popoverSize.height + PositionPadding > screenSize.height
        ? screenSize.height - popoverSize.height - PositionPadding
        : props.position.top;
    return { top, left };
  }, [popoverSize.width, popoverSize.height, props.position, screenSize.width, screenSize.height]);

  const handleChange = (color: string) => {
    if (props.color !== color) {
      props.onChange(color);
    }
  };

  const debouncedHandleChange = useMemo(
    () =>
      debounce((color: string) => {
        handleChange(color);
      }, 200),
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedHandleChange.cancel();
    };
  }, [debouncedHandleChange]);

  const handleReset = () => {
    if (props.color !== undefined) {
      props.onChange(undefined);
    }
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = ensureHexPrefix(event.target.value);
    setInputValue(newValue);

    // Only update color state and trigger onChange if valid HEX
    if (isValidHexColor(newValue)) {
      setColor(newValue);
      debouncedHandleChange(newValue);
    }
  };

  const handleBackdropMouseUp = () => {
    isSelecting.current = false;
    handleChange(color);
  };

  return (
    <>
      <div className={styles.backdrop} onMouseUp={handleBackdropMouseUp} />
      <div ref={popover} className={styles.colorPopover} style={position}>
        <HexColorPicker
          color={color}
          onChange={(newColor) => {
            setColor(newColor);
            setInputValue(newColor);
          }}
          onMouseDown={() => {
            isSelecting.current = true;
          }}
          onMouseUp={() => {
            isSelecting.current = false;
            handleChange(color);
          }}
        />
        {props.presetColors?.length !== undefined && props.presetColors.length > 0 && (
          <div className={styles.colorSwatches}>
            {props.presetColors.map((presetColor) => (
              <button
                key={presetColor}
                className={classNames(styles.colorSwatch, {
                  [styles.selected]: props.color === presetColor,
                })}
                style={{ background: presetColor }}
                onClick={() => handleChange(presetColor)}
              />
            ))}
          </div>
        )}
        <div className={styles.colorInput}>
          <Input type="text" value={inputValue} autoFocus onChange={handleInputChange} />
        </div>
        <div className={styles.buttons}>
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              props.onClose();
            }}>
            Done
          </Button>
          <InlineButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleReset();
            }}>
            Reset
          </InlineButton>
        </div>
      </div>
    </>
  );
};

export const ColorPicker = forwardRef<HTMLDivElement, ColorPickerProps>(
  function ColorPicker(props, ref) {
    const { color, presetColors, size = 'medium', label, onChange, className } = props;
    const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
    const colorLabelRef = useRef<HTMLDivElement>(null);

    const handleClick = () => {
      if (colorLabelRef.current) {
        const rect = colorLabelRef.current.getBoundingClientRect();
        setPosition({ top: rect.bottom + 8, left: rect.left });
      }
    };

    return (
      <div ref={ref} className={classNames(styles.colorPicker, className)} onClick={handleClick}>
        <div className={styles.colorLabel} ref={colorLabelRef}>
          <div
            className={classNames(styles.colorToggle, {
              [styles.toggleSmall]: size === 'small',
              [styles.toggleLarge]: size === 'large',
            })}
            style={{ backgroundColor: color }}
          />
          {label}
        </div>
        {position !== null && (
          <Overlay>
            <Popover
              color={color}
              presetColors={presetColors}
              position={position}
              onChange={onChange}
              onClose={() => setPosition(null)}
            />
          </Overlay>
        )}
      </div>
    );
  },
);
